import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LoadingService } from './core/services/loading.service';
import { Title } from '@angular/platform-browser';
import { filter, firstValueFrom, map } from 'rxjs';
import { PrimeNG } from 'primeng/config';
import { SharedModule } from './shared/shared.module';
import { TranslateService } from '@ngx-translate/core';
import { InactivityService } from './core/services/inactivity.service';
import { PermissionService } from './core/services/permission.service';
import { DkCookieConsentComponent } from '@/app/shared/components/dk-cookie-consent/dk-cookie-consent.component';
import { UpdateNotificationComponent } from '@/app/shared/components/update-notification/update-notification.component';
import { ServiceWorkerService } from './core/services/serviceworker.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    standalone: true,
    imports: [RouterOutlet, SharedModule, DkCookieConsentComponent, UpdateNotificationComponent],
})
export class AppComponent implements OnInit {
    constructor(
        public router: Router,
        public loadingService: LoadingService,
        private activatedRoute: ActivatedRoute,
        private titleService: Title,
        private primeng: PrimeNG,
        private translateService: TranslateService,
        private inactivityService: InactivityService,
        private permissionService: PermissionService,
        private serviceWorkerService: ServiceWorkerService
    ) {
        this.serviceWorkerService.checkForUpdates();

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(async (event: any) => {
            if (!event.url.includes('/login') && !event.url.includes('/public')) {
                this.inactivityService.startWatching();
            } else {
                this.inactivityService.stopWatching();
            }
            await firstValueFrom(this.permissionService.permissions$);

            // // Ana sayfa yönlendirmesi için yetki kontrolü
            // if (event.url === '/' || event.url === '/users') {
            //   const hasUsersPermission = this.permissionService.hasPermission('users_get');
            //   if (!hasUsersPermission) {
            //     const firstAccessiblePage = this.permissionService.getFirstAccessiblePage();
            //     console.log(firstAccessiblePage);
            //     this.router.navigate([firstAccessiblePage]);
            //   }
            // }

            // // Diğer sayfalar için genel yetki kontrolü
            // if (
            //   event.url !== '/' &&
            //   event.url !== '/users' &&
            //   !event.url.includes('/login') &&
            //   !event.url.includes('/public')
            // ) {
            //   const currentRoute = event.url.split('/').pop(); // URL'den sayfa adını al

            //   const hasPermission =
            //     this.permissionService.hasPermission(`${currentRoute}_get`) ||
            //     this.permissionService.hasPermission(`${currentRoute}s_get`) ||
            //     this.permissionService.hasPermission(`${currentRoute.replace('-', '')}_get`) ||
            //     this.permissionService.hasPermission(`${currentRoute.replace('-', '')}s_get`);

            //   if (!hasPermission) {
            //     const firstAccessiblePage = this.permissionService.getFirstAccessiblePage();
            //     this.router.navigate([firstAccessiblePage]);
            //   }
            // }
            // console.log(event.url);
        });
    }

    ngOnInit() {
        this.primeng.ripple.set(true);

        const lang = localStorage.getItem('lang') || 'en';
        this.setPrimeNgLocale(lang);
        this.translate(lang);

        this.translateService.onLangChange.subscribe((event) => {
            this.setPrimeNgLocale(event.lang);
        });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => {
                    let route = this.activatedRoute;
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route.snapshot.data['title'];
                })
            )
            .subscribe((title: string) => {
                title = this.translateService.instant(title || 'users');
                this.titleService.setTitle(title + (title ? ' | ' : '') + 'Backoffice');
            });

        this.inactivityService.startWatching();
    }

    setPrimeNgLocale(lang: string) {
        if (lang === 'tr') {
            this.primeng.setTranslation({
                dayNames: ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'],
                dayNamesShort: ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'],
                dayNamesMin: ['Pz', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'],
                monthNames: [
                    'Ocak',
                    'Şubat',
                    'Mart',
                    'Nisan',
                    'Mayıs',
                    'Haziran',
                    'Temmuz',
                    'Ağustos',
                    'Eylül',
                    'Ekim',
                    'Kasım',
                    'Aralık',
                ],
                monthNamesShort: ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'],
                today: 'Bugün',
                clear: 'Temizle',
                dateFormat: 'dd.mm.yy',
                firstDayOfWeek: 1,
                weekHeader: 'Hf',
            });
        } else {
            this.primeng.setTranslation({
                dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
                monthNames: [
                    'January',
                    'February',
                    'March',
                    'April',
                    'May',
                    'June',
                    'July',
                    'August',
                    'September',
                    'October',
                    'November',
                    'December',
                ],
                monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                today: 'Today',
                clear: 'Clear',
                dateFormat: 'mm/dd/yy',
                firstDayOfWeek: 0,
                weekHeader: 'Wk',
            });
        }
    }

    translate(lang: string) {
        this.translateService.setDefaultLang(lang);
        this.translateService.use(lang);
        this.translateService.get('primeng').subscribe((res) => this.primeng.setTranslation(res));
    }
}
